import { useState, useEffect, useRef, useCallback } from "react";
import MainLayout from "@/components/layout/MainLayout";
import FileUploader from "@/components/upload/FileUploader";
import TemplateSelector from "@/components/templates/TemplateSelector";
import TemplateSelectorModal from "@/components/templates/TemplateSelectorModal";
import SelectedFileCard from "@/components/upload/SelectedFileCard";
import SelectedTemplateCard from "@/components/templates/SelectedTemplateCard";
import { WizardProvider, useWizardContext } from "@/contexts/WizardContext";
import { Template } from "@/types";
import { Button } from "@/components/ui/button";

import { useQueryClient } from "@tanstack/react-query";

import { useNotifications } from "@/contexts/NotificationContext";
import { useAuth } from "@/contexts/UnifiedAuthContext";
import { useGrafanaFaro } from "@/contexts/GrafanaFaroContext";
import { useNavigate } from "react-router-dom";
import StepperComponent from "@/components/common/StepperComponent";
import { createTusFileUploader } from "@/lib/tusFileUploader";
import { cn, formatMinutes } from "@/lib/utils";
import { Progress } from "@/components/ui/progress";
import { supabase } from "@/integrations/supabase/client";
import { useNavigationBlocker } from "@/contexts/NavigationBlockerContext";
import { NavigationBlockDialog } from "@/components/common/NavigationBlockDialog";
import { CancelUploadDialog } from "@/components/common/CancelUploadDialog";
import { ProcessingStartDialog } from "@/components/common/ProcessingStartDialog";
import { useProfile } from "@/hooks/useProfile";
import { useMediaDuration } from "@/hooks/useMediaDuration";
import { AlertTriangle } from "lucide-react";
import { useTranslation } from "react-i18next";

const IndexContent = () => {
  const { t } = useTranslation();
  const {
    selectedFile,
    setSelectedFile,
    selectedTemplate,
    setSelectedTemplate,
    resetWizard,
    goToPreviousStep,
    canGoBack,
  } = useWizardContext();

  const [isProcessing, setIsProcessing] = useState(false);
  const { addNotification } = useNotifications();
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { getTraceParent, withSpan } = useGrafanaFaro();

  // Добавляем состояния для отслеживания прогресса загрузки
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [uploadedFile, setUploadedFile] = useState<string | null>(null);

  // Новые состояния для улучшенного диалога
  const [uploadStartTime, setUploadStartTime] = useState<number | null>(null);
  const [estimatedTimeLeft, setEstimatedTimeLeft] = useState<string>("");
  const [elapsedTime, setElapsedTime] = useState<string>("");
  const [uploadSpeed, setUploadSpeed] = useState<string>("");
  const abortControllerRef = useRef<AbortController | null>(null);

  // Состояния для блокировки навигации
  const [showNavigationDialog, setShowNavigationDialog] = useState(false);
  const [showCancelUploadDialog, setShowCancelUploadDialog] = useState(false);
  const { blockNavigation, unblockNavigation } = useNavigationBlocker();

  // Состояние для модального диалога выбора шаблона
  const [showTemplateModal, setShowTemplateModal] = useState(false);

  // Состояние для файлового инпута
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Обработчики диалога навигации (используются в диалоге)
  const [handleNavigationCancel, setHandleNavigationCancel] = useState<
    (() => void) | null
  >(null);
  const [handleNavigationConfirm, setHandleNavigationConfirm] = useState<
    (() => void) | null
  >(null);

  // === Состояния для диалога уведомления о начале обработки ===
  const [showProcessingDialog, setShowProcessingDialog] = useState(false);
  const [processingDialogData, setProcessingDialogData] = useState({ sourceFilename: "", targetDocumentType: "" });
  const { profile, balance, updateProfile } = useProfile(user?.id);
  const { status: durationStatus, durationSeconds, retry: retryDurationCheck } = useMediaDuration(selectedFile);
  const durationMinutes = durationSeconds ? durationSeconds / 60 : null;
  const availableMinutes = typeof balance === "number" ? balance : null;
  const remainingMinutes =
    availableMinutes !== null && durationMinutes !== null
      ? availableMinutes - durationMinutes
      : null;
  const exceedsBalance = remainingMinutes !== null && remainingMinutes < 0;
  const showOverLimitWarning = durationStatus === "ready" && exceedsBalance;
  // ==========================================================

  // Ref для хранения истории прогресса (скользящее окно для расчета скорости)
  const progressHistoryRef = useRef<Array<{ time: number; progress: number }>>([]);
  const SPEED_WINDOW_SECONDS = 5; // Окно для расчета скорости

  // Refs для доступа к актуальным значениям без перезапуска интервала
  const uploadProgressRef = useRef(uploadProgress);
  uploadProgressRef.current = uploadProgress;

  const selectedFileRef = useRef(selectedFile);
  selectedFileRef.current = selectedFile;

  // Расчет прошедшего времени, оставшегося времени и скорости загрузки
  useEffect(() => {
    if (!uploadStartTime || !isUploading) {
      setEstimatedTimeLeft("");
      setElapsedTime("");
      setUploadSpeed("");
      progressHistoryRef.current = []; // Очищаем историю при остановке
      return;
    }

    // Обновляем статистику каждую секунду
    const intervalId = setInterval(() => {
      const now = Date.now();
      const elapsed = now - uploadStartTime;
      const currentProgress = uploadProgressRef.current;
      const currentFile = selectedFileRef.current;

      // Прошедшее время
      const elapsedSeconds = Math.floor(elapsed / 1000);
      const elapsedMinutes = Math.floor(elapsedSeconds / 60);
      const remainingSeconds = elapsedSeconds % 60;
      if (elapsedMinutes > 0) {
        setElapsedTime(`${elapsedMinutes}:${remainingSeconds.toString().padStart(2, '0')}`);
      } else {
        setElapsedTime(`${elapsedSeconds} сек`);
      }

      // Добавляем текущий прогресс в историю
      progressHistoryRef.current.push({ time: now, progress: currentProgress });

      // Удаляем записи старше 5 секунд
      const cutoffTime = now - SPEED_WINDOW_SECONDS * 1000;
      progressHistoryRef.current = progressHistoryRef.current.filter(
        entry => entry.time >= cutoffTime
      );

      // Скорость загрузки (если есть файл и достаточно данных)
      if (currentFile && progressHistoryRef.current.length >= 2) {
        const history = progressHistoryRef.current;
        const oldest = history[0];
        const newest = history[history.length - 1];

        const timeDiffMs = newest.time - oldest.time;
        const progressDiff = newest.progress - oldest.progress;

        if (timeDiffMs > 0 && progressDiff > 0) {
          const bytesDiff = (currentFile.size * progressDiff) / 100;
          const speedBytesPerSecond = bytesDiff / (timeDiffMs / 1000);

          // Форматируем скорость
          if (speedBytesPerSecond >= 1024 * 1024) {
            setUploadSpeed(`${(speedBytesPerSecond / (1024 * 1024)).toFixed(1)} МБ/с`);
          } else if (speedBytesPerSecond >= 1024) {
            setUploadSpeed(`${(speedBytesPerSecond / 1024).toFixed(0)} КБ/с`);
          } else {
            setUploadSpeed(`${speedBytesPerSecond.toFixed(0)} Б/с`);
          }

          // Оставшееся время на основе текущей скорости
          if (currentProgress < 100 && speedBytesPerSecond > 0) {
            const bytesRemaining = currentFile.size - (currentFile.size * currentProgress) / 100;
            const remainingSecs = Math.ceil(bytesRemaining / speedBytesPerSecond);

            if (remainingSecs >= 60) {
              const mins = Math.ceil(remainingSecs / 60);
              setEstimatedTimeLeft(formatMinutes(mins));
            } else {
              setEstimatedTimeLeft(`~${remainingSecs} сек`);
            }
          }
        }
      } else if (currentFile && currentProgress > 0) {
        // Fallback на среднюю скорость, если недостаточно данных для скользящего окна
        const bytesUploaded = (currentFile.size * currentProgress) / 100;
        const speedBytesPerSecond = bytesUploaded / (elapsed / 1000);

        if (speedBytesPerSecond >= 1024 * 1024) {
          setUploadSpeed(`${(speedBytesPerSecond / (1024 * 1024)).toFixed(1)} МБ/с`);
        } else if (speedBytesPerSecond >= 1024) {
          setUploadSpeed(`${(speedBytesPerSecond / 1024).toFixed(0)} КБ/с`);
        } else {
          setUploadSpeed(`${speedBytesPerSecond.toFixed(0)} Б/с`);
        }

        // Fallback оставшееся время
        if (currentProgress < 100 && currentProgress > 0) {
          const estimatedTotal = (elapsed / currentProgress) * 100;
          const remaining = estimatedTotal - elapsed;
          if (remaining > 0) {
            const remainingSecs = Math.ceil(remaining / 1000);
            if (remainingSecs >= 60) {
              const mins = Math.ceil(remainingSecs / 60);
              setEstimatedTimeLeft(formatMinutes(mins));
            } else {
              setEstimatedTimeLeft(`~${remainingSecs} сек`);
            }
          }
        }
      }
    }, 1000);

    return () => clearInterval(intervalId);
    // Убираем uploadProgress и selectedFile из зависимостей - используем refs
  }, [uploadStartTime, isUploading]);

  // Ref для хранения актуального состояния isUploading в callback
  const isUploadingRef = useRef(isUploading);
  isUploadingRef.current = isUploading;

  // Callback для блокировки навигации - стабильная ссылка
  const navigationCallback = useCallback(async () => {
    // Используем ref для получения актуального значения isUploading
    if (!isUploadingRef.current) {
      return true;
    }

    return new Promise<boolean>((resolve) => {
      const handleDialogCancel = () => {
        setShowNavigationDialog(false);
        setHandleNavigationCancel(null);
        setHandleNavigationConfirm(null);
        resolve(false); // Блокируем навигацию
      };

      const handleDialogConfirm = () => {
        setShowNavigationDialog(false);
        setHandleNavigationCancel(null);
        setHandleNavigationConfirm(null);
        // Отменяем загрузку
        if (abortControllerRef.current) {
          abortControllerRef.current.abort();
        }
        resolve(true); // Разрешаем навигацию
      };

      // Сохраняем обработчики в состоянии
      setHandleNavigationCancel(() => handleDialogCancel);
      setHandleNavigationConfirm(() => handleDialogConfirm);

      setShowNavigationDialog(true);
    });
  }, []); // Пустые зависимости - callback стабильный

  // Управление блокировкой навигации - только при изменении isUploading
  useEffect(() => {
    if (isUploading) {
      // Включаем блокировку навигации
      blockNavigation(navigationCallback);
    } else {
      // Отключаем блокировку навигации
      unblockNavigation();
    }

    // Cleanup функция для отключения блокировки при размонтировании компонента
    return () => {
      unblockNavigation();
    };
    // Исключаем navigationCallback из зависимостей, т.к. он стабилен (пустые deps)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUploading, blockNavigation, unblockNavigation]);

  // Fallback: handle returnTo if authStore didn't catch it
  useEffect(() => {
    if (user) {
      const returnTo = localStorage.getItem('returnTo');
      if (returnTo) {
        localStorage.removeItem('returnTo');
        navigate(returnTo);
      }
    }
  }, [user, navigate]);

  const uploadFile = async (file: File) => {
    if (!user || !selectedTemplate) return;

    // Создаем новый AbortController для отмены загрузки
    abortControllerRef.current = new AbortController();

    try {
      setIsUploading(true);
      setUploadProgress(0);
      setUploadStartTime(Date.now());

      // Загружаем файл через TUS протокол (chunked upload)
      const { uploadFile } = createTusFileUploader({
        supabaseUrl: import.meta.env.VITE_SUPABASE_URL,
        supabaseKey: import.meta.env.VITE_SUPABASE_ANON_KEY,
        supabaseBucket: "public-files",
      });

      const { url, key } = await uploadFile(
        file,
        (progress) => {
          console.log(`${t('index.upload.progress_label')}: ${progress}%`); // "Прогресс загрузки" -> localized or just log
          setUploadProgress(progress);
        },
        abortControllerRef.current?.signal
      );

      // Проверяем, не была ли загрузка отменена
      if (abortControllerRef.current?.signal.aborted) {
        throw new Error(t('index.upload.cancelled'));
      }

      // Создаем запись в базе данных
      const result = withSpan?.('file_upload', () => {
        const traceParent = getTraceParent();
        console.log('[Index] traceParent from getTraceParent():', traceParent);

        // Собираем metadata объект (гибкость для будущего расширения)
        const metadata: Record<string, string> = {};
        if (traceParent) {
          metadata.traceparent = traceParent;
        }
        // В будущем можно добавить:
        // metadata.user_agent = navigator.userAgent;
        // metadata.screen_size = `${window.innerWidth}x${window.innerHeight}`;

        // console.log('[Index] Calling create_record with:', {
        //   name: file.name,
        //   template_id: selectedTemplate.id,
        //   file_path: key,
        //   p_metadata: metadata,
        // });

        return supabase.rpc("create_record", {
          name: file.name,
          template_id: selectedTemplate.id,
          file_path: key,
          p_metadata: metadata,
        });
      });

      const { data, error } = await result!;

      if (error) throw error;

      setUploadedFile(url);

      // Инвалидируем кэш записей перед показом уведомления и переходом
      await queryClient.invalidateQueries({ queryKey: ["records"] });

      addNotification({
        userId: user.id,
        type: "success",
        message: t('index.upload.success'),
      });

      // === Логика показа диалога начала обработки ===
      const userSettings = profile?.user_settings as { hide_processing_start_notification?: boolean } | null;
      const hideNotification = userSettings?.hide_processing_start_notification;

      if (hideNotification) {
        navigate("/dashboard");
      } else {
        setProcessingDialogData({
          sourceFilename: file.name,
          targetDocumentType: selectedTemplate.name
        });
        setShowProcessingDialog(true);
      }
      // ============================================

      return { url, key };
    } catch (error) {
      if (error instanceof Error && error.message === t('index.upload.cancelled')) {
        addNotification({
          userId: user.id,
          type: "info",
          message: t('index.upload.cancelled'),
        });
        setIsProcessing(false); // Скрываем диалог обработки
        resetWizard(); // Возвращаемся к началу при отмене
      } else {
        console.error("Error uploading file:", error);
        addNotification({
          userId: user.id,
          type: "error",
          message: t('index.upload.error'),
        });
      }
      throw error;
    } finally {
      setIsUploading(false);
      setIsProcessing(false); // Всегда скрываем диалог обработки
      setUploadStartTime(null);
      setEstimatedTimeLeft("");
      abortControllerRef.current = null;
    }
  };

  const handleCancelUpload = () => {
    // Показываем диалог подтверждения отмены
    setShowCancelUploadDialog(true);
  };

  const handleCancelUploadConfirm = () => {
    setShowCancelUploadDialog(false);
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    // setIsProcessing и resetWizard будут вызваны автоматически
    // в finally блоке uploadFile при обработке отмены
  };

  const handleCancelUploadCancel = () => {
    setShowCancelUploadDialog(false);
  };

  // === Обработчик подтверждения диалога ===
  const handleDialogConfirm = (dontShowAgain: boolean) => {
    if (dontShowAgain) {
      updateProfile({
        user_settings: {
          ...(profile?.user_settings as Record<string, unknown> || {}),
          hide_processing_start_notification: true,
        },
      });
    }
    setShowProcessingDialog(false);
    navigate("/dashboard");
  };
  // ========================================

  const steps = [
    {
      id: 1,
      title: t('index.steps.select_file.title'),
      description: t('index.steps.select_file.desc'),
      icon: "📁",
    },
    {
      id: 2,
      title: t('index.steps.select_template.title'),
      description: t('index.steps.select_template.desc'),
      icon: "📋",
    },
    {
      id: 3,
      title: t('index.steps.processing.title'),
      description: t('index.steps.processing.desc'),
      icon: "⚙️",
    },
  ];

  // Определяем текущий шаг на основе состояния
  const getCurrentStep = () => {
    if (!selectedFile) return 1;
    if (selectedFile && !selectedTemplate) return 2;
    if (selectedFile && selectedTemplate) return 3;
    return 1;
  };

  const handleFileSelect = (file: File | null) => {
    setSelectedFile(file);
    setSelectedTemplate(null);
  };

  const handleTemplateSelect = (template: Template) => {
    setSelectedTemplate(template);
  };

  const handleProcessFile = async () => {
    if (!selectedFile || !selectedTemplate || !user) return;

    if (exceedsBalance) {
      navigate("/balance");
      return;
    }

    setIsProcessing(true);
    try {
      await uploadFile(selectedFile);
    } catch (error) {
      // Не показываем ошибку в консоли если загрузка была отменена пользователем
      if (error instanceof Error && error.message === t('index.upload.cancelled')) {
        // Ошибка отмены уже обработана в uploadFile, включая setIsProcessing(false)
        return;
      }
      console.error("Error starting upload:", error);
      setIsProcessing(false);
    }
  };

  const handleChangeFile = () => {
    setSelectedFile(null);
    setSelectedTemplate(null);
    // Очищаем значение file input, чтобы onChange сработал при повторном выборе того же файла
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleStepClick = (stepId: number) => {
    if (stepId === 1) {
      // Переход к первому шагу
      setSelectedFile(null);
      setSelectedTemplate(null);
      // Очищаем значение file input
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    } else if (stepId === 2 && selectedFile) {
      // Переход ко второму шагу (только если файл выбран)
      setSelectedTemplate(null);
    }
    // Третий шаг недоступен для клика, так как это финальный процесс
  };

  // Обработчики для интерактивных кнопок степпера
  const handleStepperFileSelect = () => {
    fileInputRef.current?.click();
  };

  const handleStepperTemplateSelect = () => {
    setShowTemplateModal(true);
  };

  const handleStepperProcessFile = async () => {
    if (selectedFile && selectedTemplate) {
      await handleProcessFile();
    }
  };

  const handleTemplateModalSelect = (template: Template) => {
    setSelectedTemplate(template);
    setShowTemplateModal(false);
  };

  const validateFile = (file: File): boolean => {
    const FILE_SIZE_LIMIT = 5 * 1024 * 1024 * 1024; // 5GB

    if (file.size > FILE_SIZE_LIMIT) {
      addNotification({
        userId: user?.id || "",
        type: "error",
        message: t('index.validations.size_error'),
      });
      return false;
    }

    if (!file.type.startsWith("audio/") && !file.type.startsWith("video/")) {
      addNotification({
        userId: user?.id || "",
        type: "error",
        message: t('index.validations.type_error'),
      });
      return false;
    }

    return true;
  };

  const handleStepperFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      if (validateFile(file)) {
        setSelectedFile(file);
        setSelectedTemplate(null); // Сбрасываем шаблон при выборе нового файла
      }
    }
  };

  const OverLimitBanner = ({ className = "" }: { className?: string }) => {
    if (
      !showOverLimitWarning ||
      !selectedFile ||
      availableMinutes === null ||
      durationMinutes === null
    ) {
      return null;
    }

    const missingMinutes =
      remainingMinutes !== null ? Math.ceil(Math.abs(remainingMinutes)) : null;

    return (
      <div
        className={cn(
          "rounded-lg border border-destructive/30 bg-destructive/10 p-4 space-y-2",
          className
        )}
      >
        <div className="flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-destructive" />
          <div className="space-y-1 text-sm">
            <p className="text-destructive font-semibold">{t('index.balance.warning')}</p>
            <p className="text-muted-foreground">
              {t('index.balance.desc', { duration: formatMinutes(Math.ceil(durationMinutes)), available: formatMinutes(availableMinutes) })}
            </p>
            {missingMinutes !== null && missingMinutes > 0 && (
              <p className="text-muted-foreground">
                {t('index.balance.missing', { missing: formatMinutes(missingMinutes) })}
              </p>
            )}
          </div>
        </div>
        <div className="flex justify-start pt-1">
          <Button size="sm" onClick={() => navigate("/balance")}>
            {t('index.balance.btn')}
          </Button>
        </div>
      </div>
    );
  };

  const FileSummarySection = ({
    title,
    className = "",
    titleClassName = "text-xs text-muted-foreground mb-1",
  }: {
    title?: string;
    className?: string;
    titleClassName?: string;
  }) => {
    if (!selectedFile) return null;

    return (
      <div className={cn("space-y-2", className)}>
        {title && <p className={titleClassName}>{title}</p>}
        <SelectedFileCard
          file={selectedFile}
          durationStatus={durationStatus}
          durationMinutes={durationMinutes}
          availableMinutes={availableMinutes}
          remainingMinutes={remainingMinutes}
          onRetry={retryDurationCheck}
        />
        <OverLimitBanner className="mt-3" />
      </div>
    );
  };

  const renderMobileStepContent = (stepId: number) => {
    if (stepId === 1 && selectedFile) {
      const showResetButton = !showOverLimitWarning;
      return {
        replaceDefaultCta: true,
        content: (
          <div className="rounded-2xl border border-border bg-background p-3 shadow-sm space-y-3">
            <FileSummarySection className="space-y-2" />
            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant="outline" onClick={handleStepperFileSelect}>
                {t('index.mobile.change_file')}
              </Button>
              {showResetButton && (
                <Button size="sm" variant="ghost" onClick={handleChangeFile}>
                  {t('index.mobile.clear_selection')}
                </Button>
              )}
            </div>
          </div>
        ),
      };
    }

    if (stepId === 2 && selectedTemplate) {
      return {
        content: (
          <div className="rounded-2xl border border-border bg-card/60 p-3 space-y-2">
            <p className="text-xs font-medium text-muted-foreground">{t('index.mobile.selected_template')}</p>
            <SelectedTemplateCard template={selectedTemplate} />
          </div>
        ),
      };
    }

    return null;
  };

  return (
    <MainLayout
      onBackClick={canGoBack ? goToPreviousStep : undefined}
      showBackButton={canGoBack}
    >
      {isProcessing ? (
        // Экран загрузки с правильным выравниванием
        <div className="container-page">
          <div className="max-w-4xl mx-auto">
            <div className="flex flex-col min-h-[500px]">
              {/* Заголовок */}
              <div className="text-center py-6 border-b bg-card">
                <div className="space-y-4">
                  <div>
                    <h2 className="text-lg font-medium">{t('index.upload.uploading_title')}</h2>
                    <p className="text-muted-foreground text-sm mt-1">
                      {t('index.upload.uploading_desc')}
                    </p>
                  </div>

                  {/* Прогресс-бар */}
                  <div className="max-w-md mx-auto">
                    <Progress value={uploadProgress} className="h-2" />
                    <div className="flex justify-between items-center text-xs mt-2">
                      <span className="text-muted-foreground">
                        {Math.round(uploadProgress)}% {t('index.upload.progress_label')}
                      </span>
                      {uploadSpeed && (
                        <span className="text-muted-foreground">
                          {uploadSpeed}
                        </span>
                      )}
                    </div>
                    {/* Дополнительная статистика */}
                    {(elapsedTime || estimatedTimeLeft) && (
                      <div className="flex justify-between items-center text-xs mt-1">
                        {elapsedTime && (
                          <span className="text-muted-foreground">{t('index.upload.elapsed')}: {elapsedTime}</span>
                        )}
                        {estimatedTimeLeft && (
                          <span className="text-brand-600 font-medium">{t('index.upload.remaining')} {estimatedTimeLeft}</span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Основной контент */}
              <div className="flex-1 py-4 space-y-4 overflow-y-auto">
                {/* Информация о файле и шаблоне */}
                {selectedFile && selectedTemplate && (
                  <div className="space-y-3">
                    {/* Файл */}
                    <div className="bg-card border border-border rounded-lg p-3">
                      <p className="text-xs font-medium text-muted-foreground mb-2">
                        {t('index.file_info.file_label')}
                      </p>
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-brand-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <span className="text-brand-600 text-xs font-bold">
                            {selectedFile.name.split(".").pop()?.toUpperCase()}
                          </span>
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="font-medium text-foreground text-sm leading-tight">
                            {selectedFile.name}
                          </p>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            {(selectedFile.size / (1024 * 1024)).toFixed(1)} МБ
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Шаблон */}
                    <div className="bg-card border border-border rounded-lg p-3">
                      <p className="text-xs font-medium text-muted-foreground mb-2">
                        {t('index.file_info.template_label')}
                      </p>
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <span className="text-green-600 text-sm">📄</span>
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="font-medium text-foreground text-sm leading-tight">
                            {selectedTemplate.name}
                          </p>
                          <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
                            {selectedTemplate.description}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Предупреждение */}
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <div className="flex gap-3">
                    <div className="text-amber-600 flex-shrink-0">⚠️</div>
                    <div className="text-sm text-amber-800">
                      <p className="font-medium">{t('index.warning.dont_close')}</p>
                      <p className="mt-0.5">
                        {t('index.warning.desc')}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Нижняя панель с кнопкой */}
              <div className="border-t bg-card p-4 mt-auto flex-shrink-0">
                <div className="max-w-md mx-auto">
                  <Button
                    variant="outline"
                    onClick={handleCancelUpload}
                    className="w-full text-red-600 dark:text-red-400 border-red-200 dark:border-red-800 hover:bg-red-50 dark:hover:bg-red-950 hover:border-red-300 dark:hover:border-red-700"
                    size="lg"
                  >
                    {t('index.upload.cancel_btn')}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        // Обычный интерфейс мастера
        <div className="container-page">
          {/* Компактный заголовок на втором+ шаге */}
          {selectedFile ? (
            <div className="text-center mb-6">
              <h1 className="text-xl font-bold mb-1">
                {t('index.hero.title')}
              </h1>
            </div>
          ) : (
            <div className="text-center mb-8">
              <h1 className="text-2xl font-bold mb-2">
                {t('index.hero.title')}
              </h1>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                {t('index.hero.subtitle')}
              </p>
            </div>
          )}

          <div className="max-w-4xl mx-auto">
            <div className="space-y-6">
              <StepperComponent
                currentStep={getCurrentStep()}
                steps={steps}
                onStepClick={handleStepClick}
                onFileSelect={handleStepperFileSelect}
                onTemplateSelect={handleStepperTemplateSelect}
                onProcessFile={handleStepperProcessFile}
                selectedFileName={selectedFile?.name}
                selectedTemplateName={selectedTemplate?.name}
                isProcessing={isUploading}
                renderMobileContent={renderMobileStepContent}
              />

              {/* Отображение только для десктопной версии */}
              <div className="hidden md:block space-y-6">
                {/* Отображение выбранного файла в компактном виде только на втором шаге */}
                {selectedFile && !selectedTemplate && (
                  <FileSummarySection
                    title={t('index.selected_file.title')}
                    titleClassName="text-xs text-muted-foreground mb-2"
                  />
                )}

                {!selectedFile && (
                  <div>
                    <FileUploader
                      onFileSelect={handleFileSelect}
                      selectedFile={selectedFile}
                      uploadProgress={uploadProgress}
                      isUploading={isUploading}
                    />
                  </div>
                )}

                {selectedFile && !selectedTemplate && (
                  <div>
                    <TemplateSelector
                      onSelect={handleTemplateSelect}
                      initialTemplateId={selectedTemplate?.id}
                    />
                  </div>
                )}

                {selectedFile && selectedTemplate && !uploadedFile && (
                  <div>
                    <div className="text-center mb-4">
                      <h2 className="text-lg font-medium">{t('index.ready_to_process')}</h2>
                    </div>

                    {/* Компактная сводка */}
                    <div className="space-y-3 mb-6">
                      <FileSummarySection title={t('index.file_info.file_label')} />
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">{t('index.template_label')}</p>
                        <SelectedTemplateCard template={selectedTemplate} />
                      </div>
                    </div>

                    <div className="flex justify-center gap-3">
                      <Button variant="outline" onClick={resetWizard}>
                        {t('common.cancel')}
                      </Button>
                      <Button
                        onClick={() => handleProcessFile()}
                        disabled={isUploading}
                        size="lg"
                        className="px-8"
                      >
                        {isUploading ? t('index.process_btn_loading') : t('index.process_btn')}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <NavigationBlockDialog
        isOpen={showNavigationDialog}
        onCancel={
          handleNavigationCancel ||
          (() => {
            console.log("[Index] Fallback onCancel для диалога навигации");
            setShowNavigationDialog(false);
          })
        }
        onConfirm={
          handleNavigationConfirm ||
          (() => {
            console.log("[Index] Fallback onConfirm для диалога навигации");
            setShowNavigationDialog(false);
          })
        }
        uploadProgress={uploadProgress}
        fileName={selectedFile?.name}
        fileSize={selectedFile?.size}
        templateName={selectedTemplate?.name}
      />
      <CancelUploadDialog
        isOpen={showCancelUploadDialog}
        onCancel={handleCancelUploadCancel}
        onConfirm={handleCancelUploadConfirm}
        uploadProgress={uploadProgress}
        fileName={selectedFile?.name}
        fileSize={selectedFile?.size}
        templateName={selectedTemplate?.name}
      />

      {/* Скрытый файловый инпут для степпера */}
      <input
        type="file"
        className="hidden"
        accept="audio/*,video/*,.mp3,.wav,.ogg,.m4a,.aac,.flac,.mp4,.mov,.avi,.mkv,.webm,.wmv"
        ref={fileInputRef}
        onChange={handleStepperFileChange}
      />

      {/* Модальный диалог выбора шаблона */}
      <TemplateSelectorModal
        isOpen={showTemplateModal}
        onClose={() => setShowTemplateModal(false)}
        onSelect={handleTemplateModalSelect}
        selectedTemplateId={selectedTemplate?.id}
      />

      <ProcessingStartDialog
        open={showProcessingDialog}
        onOpenChange={(open) => {
          if (!open) {
            // Если диалог закрывают без подтверждения (например клик вне области),
            // считаем это как "продолжить без сохранения настроек"
            setShowProcessingDialog(false);
            navigate("/dashboard");
          }
        }}
        onConfirm={handleDialogConfirm}
        sourceFilename={processingDialogData.sourceFilename}
        targetDocumentType={processingDialogData.targetDocumentType}
      />
    </MainLayout>
  );
};

const Index = () => {
  return (
    <WizardProvider>
      <IndexContent />
    </WizardProvider>
  );
};

export default Index;
